"use client";

import { Tooltip, useMediaQuery, VeBox, VeTypography } from "../../mui";
import { limiter } from "../../hoc";
import { StatusTag } from "../StatusTag/statusTag";
import { useState } from "react";
import { ConfirmationModal } from "../Modal/confirmationModal";
import { VeTooltip } from "../VeTooltip/VeTooltip";

interface FileInfo {
  s3ObjectKey: string;
  fileSize: number;
  status: "ACTIVE" | "IN_ACTIVE";
  originalFileName: string;
  commentStatus?: string;
  comment?: string;
  objectKey?: string;
  docType?: string;
  isValid?: boolean;
  error?: string;
  displayName?: string;
  url?: string;
}

type UploadDocumentCardTypes = {
  image: any;
  item: FileInfo;
  fileTypeImages: any;
  setFileSelected: any;
  softDelete?: boolean;
  edit?: boolean;
  admin?: boolean;
  setFileKey?: any;
  data?: any;
  isShareholder?: any;
  flatFilesSelected?: any;
  showError?: boolean;
  multiple?: boolean;
  fileSizeRequired?: boolean;
  deleteFile?: boolean;
  downloadText?: boolean;
  downloadOntextClick?: boolean;
  showDeleteConfirmationModel?: boolean;
  cmsUpload?: boolean;
  deleteSuccess?: () => void;
  noPermission?: boolean;
};

export const UploadDocumentCard = ({
  image,
  item,
  fileTypeImages,
  setFileSelected,
  softDelete,
  edit,
  admin,
  setFileKey,
  data,
  showError = false,
  multiple = false,
  fileSizeRequired = true,
  deleteFile = true,
  downloadText = false,
  downloadOntextClick = false,
  showDeleteConfirmationModel = false,
  deleteSuccess = () => { },
  noPermission = false,
  cmsUpload = false,
}: UploadDocumentCardTypes) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const isMobile = useMediaQuery("(max-width: 600px)");
  const fileName =
    item?.originalFileName || item?.displayName ||
    item?.url?.substring(item?.url?.lastIndexOf("/") + 1) ||
    "";
  const finalSource =
    fileName?.split(".")[fileName?.split(".").length - 1] === "jpeg" ||
    fileName?.split(".")[fileName?.split(".").length - 1] === "png" ||
    fileName?.split(".")[fileName?.split(".").length - 1] === "svg" ||
    fileName?.split(".")[fileName?.split(".").length - 1] === "jpg"
      ? fileTypeImages["img"]
      : fileTypeImages[
          `${fileName?.split(".")[fileName?.split(".").length - 1]}`
        ] || fileTypeImages["img"];

  const validFile = item?.isValid ?? true;

  const handleDownload = () => {
    console.log("download");
    setFileKey({
      actionType: "DOWNLOAD",
      key: item.objectKey ? item?.objectKey : item?.s3ObjectKey,
    });
  };

  const inActiveDelete = data
    ? data?.some(
        (ele: any) =>
          ele.s3ObjectKey === item.s3ObjectKey ||
          ele?.objectKey === item?.objectKey
      )
    : false;
  const handleDeleteDocument = () => {
    if (!validFile) {
      setFileSelected((prevState: any) =>
        prevState.filter((file: any) => file.objectKey !== item.objectKey)
      );
      return;
    }
    if (
      (softDelete && data?.length > 0) ||
      (inActiveDelete && data?.length > 0)
    ) {
      // Does not remove the document , but rather changes its status to 'IN_ACTIVE'
      // alert("CHANGES STATUS TO INACTIVE");
      setFileSelected((prevState: FileInfo[]) =>
        prevState.map((f: FileInfo) =>
          f.s3ObjectKey === item.s3ObjectKey || f?.objectKey === item?.objectKey
            ? { ...f, status: "IN_ACTIVE" }
            : f
        )
      );
    } else {
      // alert("DELETES FILE");
      if (setFileKey) {
        setFileKey({
          actionType: "DELETE",
          key: item?.objectKey ? item.objectKey : item.s3ObjectKey,
        });
      }
      multiple
        ? setFileSelected((prevState: FileInfo[]) =>
            prevState.filter(
              (f: FileInfo) =>
                f.s3ObjectKey !== item.s3ObjectKey ||
                f?.objectKey !== item?.objectKey
            )
          )
        : setFileSelected([]);
    }
  };
  const download = item?.objectKey ? item?.objectKey : item?.s3ObjectKey;

  function formatFileSize(sizeInBytes: number) {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} Bytes`;
    } else if (sizeInBytes < 1048576) {
      return `${(sizeInBytes / 1024).toFixed(2)} KB`;
    } else if (sizeInBytes < 1073741824) {
      return `${(sizeInBytes / 1048576).toFixed(2)} MB`;
    } else {
      return `${(sizeInBytes / 1073741824).toFixed(2)} GB`;
    }
  }

  return (
    <>
      <VeBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          border:
            (showError && item?.commentStatus === "UNRESOLVED") || !validFile
              ? "1px solid #DE1135"
              : "1px solid rgba(202, 202, 202, 1)",
          borderRadius: "6px",
          padding: "8px !important",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <VeBox display={"flex"} alignItems={"flex-start"} gap={"12px"}>
          {image(finalSource, "icon")}
          <VeBox display="flex" flexDirection="column" gap={"2px"} lineHeight={"1rem"}>
            <VeTooltip
              title={
                fileName?.length > 15 ? (
                  <VeTypography
                    sx={{
                      padding: "4px 8px",
                      display: "flex",
                      alignItems: "center",
                    }}
                    variant="label4MainWhite"
                  >
                    {fileName}
                  </VeTypography>
                ) : (
                  ""
                )
              }
              placement="bottom"
              arrow
            >
              <VeTypography
                variant="label3Secondary"
                onClick={downloadOntextClick ? handleDownload : () => {}}
              >
                {limiter({ strings: fileName, limit: 15 })}
              </VeTypography>
            </VeTooltip>
            {downloadText && !noPermission && (
              <VeBox
                onClick={handleDownload}
                sx={{
                  cursor: "pointer",
                }}
              >
                <VeTypography
                  sx={{
                    ":hover": {
                      color: "var(--vc-violet) !important",
                      textDecoration: "underlined !important",
                      lineHeight: "1rem !important",
                    },
                  }}
                  variant="label4Disabled"
                >
                  Click to download
                </VeTypography>
              </VeBox>
            )}
          </VeBox>
        </VeBox>
        <VeBox display={"flex"} alignItems={"center"} gap={"16px"}>
          {item?.status === "IN_ACTIVE" && (
            <StatusTag bullet={false} status="danger">
              Inactive
            </StatusTag>
          )}
          {fileSizeRequired && (
            <VeTypography variant="label3Tertiary">
              {formatFileSize(item.fileSize)}
            </VeTypography>
          )}
          {!downloadText &&
            !downloadOntextClick &&
            admin &&
            download?.length! > 0 && (
              <VeBox
                onClick={handleDownload}
                sx={{ cursor: "pointer" }}
                width={"9px"}
                height={"9px"}
                display={"flex"}
                alignItems={"center"}
              >
                {image(fileTypeImages?.download?.src, "close", 9, 9)}
              </VeBox>
            )}
          {edit && deleteFile && (
            <VeBox
              onClick={() => {
                if (showDeleteConfirmationModel) {
                  setShowConfirmDelete(true); // Trigger modal
                } else {
                  handleDeleteDocument(); // Proceed directly without modal
                }
              }}
              sx={{ cursor: "pointer" }}
              // width={"9px"}
              // height={"9px"}
              display={"flex"}
              alignItems={"center"}
            >
              {image(fileTypeImages?.close?.src, "close", 16, 16)}
            </VeBox>
          )}
        </VeBox>
      </VeBox>

      {/* delete model */}
      <ConfirmationModal
        width={isMobile ? "auto" : "436px"}
        open={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        title="Delete Document"
        desc="Are you sure you want to delete this document?"
        outlinedButtonTitle="Cancel"
        containedButtonTitle="Delete"
        outlinedOnClick={() => setShowConfirmDelete(false)}
        containedOnClick={() => {
          handleDeleteDocument();
          setShowConfirmDelete(false);
          deleteSuccess();
        }}
        deleting={true}
      />
    </>
  );
};
